import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'

interface CollectedInfoDisplayProps {
  callId: Id<'calls'>
}

/**
 * Horizontal card grid showing collected information
 * Mobile-optimized with 2x2 grid that scrolls horizontally if needed
 */
export function CollectedInfoDisplay({ callId }: CollectedInfoDisplayProps) {
  const call = useQuery(api.calls.getCallById, { callId })

  const collectedData = call?.collectedData

  // Helper to check if a field has been collected
  const hasData = (value: string | undefined | null | any[]): boolean => {
    if (Array.isArray(value)) return value.length > 0
    return !!value
  }

  const cards = [
    {
      id: 'caregiver',
      icon: '👤',
      label: 'Your Name',
      value: collectedData?.caregiverName,
      hasValue: hasData(collectedData?.caregiverName),
    },
    {
      id: 'lovedOne',
      icon: '❤️',
      label: 'Loved One',
      value: collectedData?.lovedOneName
        ? `${collectedData.lovedOneName}${collectedData.relationship ? ` (${collectedData.relationship})` : ''}`
        : undefined,
      hasValue: hasData(collectedData?.lovedOneName),
    },
    {
      id: 'lovedOnePhone',
      icon: '📞',
      label: collectedData?.lovedOneName
        ? `${collectedData.lovedOneName}'s Phone`
        : "Loved One's Phone",
      value: collectedData?.lovedOnePhone ? formatPhone(collectedData.lovedOnePhone) : undefined,
      hasValue: hasData(collectedData?.lovedOnePhone),
    },
    {
      id: 'interests',
      icon: '⭐',
      label: 'Interests',
      value: collectedData?.featureInterests?.join(', '),
      hasValue: hasData(collectedData?.featureInterests),
    },
    {
      id: 'medications',
      icon: '💊',
      label: 'Medications',
      value: collectedData?.medications?.length
        ? `${collectedData.medications.length} added`
        : undefined,
      hasValue: hasData(collectedData?.medications),
    },
    {
      id: 'notifications',
      icon: '🔔',
      label: 'Notifications',
      value: [
        collectedData?.notificationPhone
          ? `📱 ${formatPhone(collectedData.notificationPhone)}`
          : null,
        collectedData?.notificationEmail ? `✉️ ${collectedData.notificationEmail}` : null,
      ]
        .filter(Boolean)
        .join(' '),
      hasValue:
        hasData(collectedData?.notificationPhone) || hasData(collectedData?.notificationEmail),
    },
  ]

  return (
    <div style={containerStyles}>
      <div style={headerStyles}>Information Collected</div>
      <div style={gridStyles}>
        {cards.map(card => (
          <div key={card.id} style={getCardStyles(card.hasValue)}>
            <div style={cardIconStyles}>{card.icon}</div>
            <div style={cardContentStyles}>
              <div style={cardLabelStyles}>{card.label}</div>
              <div style={getCardValueStyles(card.hasValue)}>
                {card.hasValue ? card.value : 'Waiting...'}
              </div>
            </div>
            {card.hasValue && <div style={checkStyles}>✓</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

// Format phone number for display
function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  const localDigits = digits.startsWith('1') ? digits.slice(1) : digits
  if (localDigits.length === 10) {
    return `(${localDigits.slice(0, 3)}) ${localDigits.slice(3, 6)}-${localDigits.slice(6)}`
  }
  return phone
}

// Styles
const containerStyles: React.CSSProperties = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  overflow: 'hidden',
}

const headerStyles: React.CSSProperties = {
  padding: '12px 16px',
  backgroundColor: '#f9fafb',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '14px',
  fontWeight: 600,
  color: '#374151',
}

const gridStyles: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '12px',
  padding: '16px',
}

const getCardStyles = (hasValue: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '12px',
  borderRadius: '10px',
  backgroundColor: hasValue ? '#f0fdf4' : '#f9fafb',
  border: `1px solid ${hasValue ? '#bbf7d0' : '#e5e7eb'}`,
  position: 'relative',
  transition: 'all 0.3s ease',
})

const cardIconStyles: React.CSSProperties = {
  fontSize: '20px',
  flexShrink: 0,
}

const cardContentStyles: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
}

const cardLabelStyles: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '4px',
}

const getCardValueStyles = (hasValue: boolean): React.CSSProperties => ({
  fontSize: '13px',
  fontWeight: hasValue ? 500 : 400,
  color: hasValue ? '#1f2937' : '#9ca3af',
  fontStyle: hasValue ? 'normal' : 'italic',
  wordBreak: 'break-word',
  lineHeight: 1.3,
})

const checkStyles: React.CSSProperties = {
  position: 'absolute',
  top: '8px',
  right: '8px',
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  backgroundColor: '#10b981',
  color: '#fff',
  fontSize: '11px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}
